import React from 'react';
import PropTypes from 'prop-types';
import { Trash2, ChevronDown } from 'lucide-react';
import POLineItemDescription from './POLineItemDescription';
import API_BASE from '../../../../utils/api.js';
import FreightDestinationSelect from '../../../shared/FreightDestinationSelect';

const proxyImg = (url) =>
  url ? `${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(url)}` : '';

export default function POLineItemCard({
  it,
  updateItem,
  removeItem,
  statusOptions,
  onOpenStatus,
  readOnly = false,
  freightDestinations = [],
  defaultShipToType = 'designer_warehouse',
  schedules = [],
}) {
  const lineTotal = Number(it.rate || 0) * Number(it.quantity || 0) + Number(it.shipping || 0);

  return (
    <div className="po-line-item if-card">
      {/* IMAGE */}
      <div className="li-col li-img">
        <div
          className="po-thumb"
          style={{ backgroundImage: it.imageUrl ? `url(${proxyImg(it.imageUrl)})` : 'none' }}
        />
      </div>

      {/* DESCRIPTION */}
      <POLineItemDescription it={it} updateItem={updateItem} readOnly={readOnly} schedules={schedules} />

      {/* COST COLUMN */}
      <div className="po-rate">
        <label>Rate</label>
        <input
          className="if-input"
          type="number"
          value={it.rate ?? 0}
          disabled={readOnly}
          onChange={(e) => updateItem(it.itemId, { rate: Number(e.target.value) })}
        />
      </div>

      <div className="po-ship">
        <label>Shipping</label>
        <input
          className="if-input"
          type="number"
          min="0"
          step="0.01"
          value={it.shipping ?? 0}
          disabled={readOnly}
          onChange={(e) => updateItem(it.itemId, { shipping: Number(e.target.value) })}
        />
      </div>

      <div className="po-destination">
        <label>Destination</label>
        <FreightDestinationSelect
          className="if-select"
          value={it.shipToType || defaultShipToType}
          address={it.shipToAddress}
          disabled={readOnly}
          freightDestinations={freightDestinations}
          onChange={(value, address) => updateItem(it.itemId, { shipToType: value, shipToAddress: address })}
        />
      </div>

      <div className="po-qty">
        <label>Qty</label>
        <input
          className="if-input"
          type="number"
          value={it.quantity ?? 0}
          disabled={readOnly}
          onChange={(e) => updateItem(it.itemId, { quantity: Number(e.target.value) })}
        />
      </div>

      <div className="po-status">
        <label>Unit</label>
        <select
          className="if-select"
          value={it.unitType || 'Each'}
          disabled={readOnly}
          onChange={(e) => updateItem(it.itemId, { unitType: e.target.value })}
        >
          <option value="Each">Each</option>
          <option value="Yards">Yards</option>
          <option value="Feet">Feet</option>
          <option value="Sq Ft">Sq Ft</option>
          <option value="Sq Yards">Sq Yards</option>
          <option value="Rolls">Rolls</option>
        </select>
      </div>

      <div className="po-status">
        <label>Status</label>
        <button
          type="button"
          className="if-select po-status-trigger"
          onClick={() => onOpenStatus && onOpenStatus(it.itemId)}
        >
          <span>{it.status || 'Draft'}</span>
          <ChevronDown size={14} className="po-status-trigger-chevron" />
        </button>
      </div>

      <div className="po-line-total">
        <label>Line Total</label>
        <div className="po-total">${lineTotal.toFixed(2)}</div>
        <div className="po-sub">
          {Number(it.rate || 0).toFixed(2)} × {Number(it.quantity || 0)}
          {Number(it.shipping || 0) > 0 && ` + $${Number(it.shipping).toFixed(2)} ship`}
        </div>
      </div>

      {/* ACTIONS */}
      {!readOnly && (
        <div className="li-col li-actions po-actions">
          <button className="remove-line" onClick={() => removeItem(it.itemId)} title="Remove item">
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

POLineItemCard.propTypes = {
  it: PropTypes.object.isRequired,
  updateItem: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
  onOpenStatus: PropTypes.func,
  freightDestinations: PropTypes.array,
  defaultShipToType: PropTypes.string,
  schedules: PropTypes.array,
};
